import BigNumber from "bignumber.js";
import { useEffect, useMemo, useState } from "react";
import { api } from "../../../../../api";
import { config } from "../../../../../config";
import { useAppSelector } from "../../../../../redux";
import { tokenService } from "../../../../../services/token.service";
import { toast } from "../../../../../toasts";
import { formatBigNumber } from "../../../../../utils";

export const usePoolsInfo = () => {
  const [usdcPoolsBalance, setUsdcPoolsBalance] = useState<string | null>(null);
  const [openPoolsHardCaps, setOpenPoolsHardCaps] = useState<string | null>(
    null
  );
  const [soilStakingBalance, setSoilStakingBalance] = useState<string | null>(
    null
  );
  const [highestAPY, setHighestAPY] = useState<string | null>(null);

  const tokenPrice = useAppSelector((store) => store.token.price);

  const getPoolsBalance = async () => {
    try {
      const {
        data: { totalDeposited },
      } = await api.pools.getGlobalPoolStats();
      setUsdcPoolsBalance(
        formatBigNumber(totalDeposited, config.USDC_TOKEN_DECIMALS)
      );
    } catch (e: unknown) {
      toast.errorHandler(e, "Unable to fetch the USDC balance of the pools");
    }
  };

  const getOpenPoolsHardCaps = async () => {
    try {
      const {
        data: { openPoolsHardCapsSum },
      } = await api.pools.getGlobalPoolStats();
      setOpenPoolsHardCaps(
        formatBigNumber(openPoolsHardCapsSum, config.USDC_TOKEN_DECIMALS)
      );
    } catch (e: unknown) {
      toast.errorHandler(e, "Unable to fetch the USDC balance of the pools");
    }
  };

  const getStakingBalance = async () => {
    try {
      const balance = await tokenService.tokenBalance(
        config.SOIL_TOKEN_ADDRESS,
        config.STAKING_ADDRESS
      );
      setSoilStakingBalance(
        formatBigNumber(balance, config.SOIL_TOKEN_DECIMALS)
      );
    } catch (e: unknown) {
      toast.errorHandler(
        e,
        "Unable to fetch the SOIL balance of the staking contract"
      );
    }
  };

  const getHighestAvailableAPY = async () => {
    try {
      const {
        data: { data },
      } = await api.pools.getLevels({
        limit: 1,
        sortBy: ["usdcApr:DESC"],
      });
      if (data.length > 0) {
        setHighestAPY(
          String(
            Number(data[0].usdcApr) / 100 + Number(data[0].highAprLimit) / 100
          ).toString()
        );
      } else {
        setHighestAPY("0");
      }
    } catch (e: unknown) {
      toast.errorHandler(e, "Unable to fetch highest apr of the pools");
    }
  };

  useEffect(() => {
    getPoolsBalance();
    getStakingBalance();
    getHighestAvailableAPY();
    getOpenPoolsHardCaps();
  }, []);

  const tvl = useMemo(() => {
    if (usdcPoolsBalance && soilStakingBalance && tokenPrice) {
      return new BigNumber(soilStakingBalance)
        .multipliedBy(tokenPrice)
        .plus(usdcPoolsBalance)
        .toFixed(0);
    }

    return null;
  }, [usdcPoolsBalance, soilStakingBalance, tokenPrice]);

  return {
    usdcPoolsBalance: usdcPoolsBalance
      ? new BigNumber(usdcPoolsBalance).toFixed(0)
      : null,
    tvl: tvl ? new BigNumber(tvl).toFixed(0) : null,
    highestAPY: highestAPY ? new BigNumber(highestAPY).toFixed(2) : null,
    openPoolsHardCaps: openPoolsHardCaps
      ? new BigNumber(openPoolsHardCaps).toFixed(0)
      : null,
  };
};
